/**
 * Adzuki.MediaUrls
 *
 * Resolves unsigned cpmaxxmediaprivate S3 URLs to short-lived presigned URLs
 * via /Media/getPublicMediaUrl/{id} (and thumbnail/caption counterparts).
 *
 * A failed lookup must never fall back to the unsigned bucket url, because S3
 * answers those with a 403 and the image or video breaks. The fallbacks instead
 * are the same-origin viewMedia* streams, or the local asset path when the
 * lookup reports the object is missing from S3 entirely.
 */

(function MediaUrls($, Adzuki)
{
	'use strict';

	var mediaUrls = {
		runId: 0,
	};

	mediaUrls._mediaId = function _mediaId(item)
	{
		return (item && (item.id || item.media_id)) || '';
	};

	mediaUrls._needsPrivateLookup = function _needsPrivateLookup(url)
	{
		if (!url || typeof url !== 'string' || url.indexOf('cpmaxxmediaprivate') === -1) {
			return false;
		}

		return !(/[?&](Signature|X-Amz-Signature)=/).test(url);
	};

	mediaUrls.needsLookup = function needsLookup(item)
	{
		return Boolean(item && mediaUrls._needsPrivateLookup(item.remote_url));
	};

	mediaUrls.needsThumbnailLookup = function needsThumbnailLookup(item)
	{
		return Boolean(item && mediaUrls._needsPrivateLookup(item.thumbnail_image));
	};

	mediaUrls.needsCaptionLookup = function needsCaptionLookup(item)
	{
		return Boolean(item && mediaUrls._needsPrivateLookup(item.captions_path));
	};

	mediaUrls.needsAnyLookup = function needsAnyLookup(item)
	{
		return mediaUrls.needsLookup(item) ||
			mediaUrls.needsThumbnailLookup(item) ||
			mediaUrls.needsCaptionLookup(item);
	};

	/**
	 * Same-origin url that streams the object through the session instead of S3.
	 *
	 * @param {Object} item
	 * @return {string}
	 */
	mediaUrls.proxyUrl = function proxyUrl(item)
	{
		const mediaId = mediaUrls._mediaId(item);

		if (!mediaId) {
			return '';
		}

		let url = `${Adzuki.base_uri}/Media/viewMediaItem/${mediaId}`;

		if (item && item.section_internal_name) {
			url += `/${encodeURIComponent(item.section_internal_name)}`;
		}

		return url;
	};

	mediaUrls.thumbnailProxyUrl = function thumbnailProxyUrl(item)
	{
		const mediaId = mediaUrls._mediaId(item);

		if (!mediaId) {
			return '';
		}

		return `${Adzuki.base_uri}/Media/viewMediaThumbnail/${mediaId}`;
	};

	mediaUrls.captionProxyUrl = function captionProxyUrl(item)
	{
		const mediaId = mediaUrls._mediaId(item);

		if (!mediaId) {
			return '';
		}

		return `${Adzuki.base_uri}/Media/viewMediaCaption/${mediaId}`;
	};

	/**
	 * Local asset url for media whose object cannot be found in either bucket.
	 * The proxy reuses the same S3 key lookup, so it would only 404 again.
	 *
	 * @param {Object} item
	 * @return {string}
	 */
	mediaUrls.localAssetUrl = function localAssetUrl(item)
	{
		const path = (item && item.path) || '';
		let fileName = '';
		let localPath;

		if (item && item.actual_file_name && item.actual_file_name.length > 1) {
			fileName = item.actual_file_name;
		} else if (item && item.original_file_name && item.original_file_name.length > 1) {
			fileName = item.original_file_name;
		}

		if (!path || !fileName) {
			return '';
		}

		localPath = String(path + fileName);

		// a private bucket url here is the unsigned url that started the problem
		if (localPath.includes('cpmaxxmediaprivate')) {
			return '';
		}

		if ((/^https?:\/\//i).test(localPath) || localPath.indexOf(Adzuki.base_uri) === 0) {
			return localPath;
		}

		return `${Adzuki.base_uri}/${localPath.replace(/^\//, '')}`;
	};

	mediaUrls.isProxyUrl = function isProxyUrl(url)
	{
		return typeof url === 'string' && url.indexOf('/Media/viewMediaItem/') !== -1;
	};

	mediaUrls.isThumbnailProxyUrl = function isThumbnailProxyUrl(url)
	{
		return typeof url === 'string' && url.indexOf('/Media/viewMediaThumbnail/') !== -1;
	};

	mediaUrls.isCaptionProxyUrl = function isCaptionProxyUrl(url)
	{
		return typeof url === 'string' && url.indexOf('/Media/viewMediaCaption/') !== -1;
	};

	mediaUrls._isS3Url = function _isS3Url(url)
	{
		return typeof url === 'string' && (/amazonaws\.com/i).test(url);
	};

	/**
	 * One-shot retry for an img or video that failed to load. A non-S3 source is
	 * left alone because the proxy resolves the same S3 key that already could
	 * not be found.
	 *
	 * @param {Object} item
	 * @return {void}
	 */
	mediaUrls.fallbackToProxy = function fallbackToProxy(item)
	{
		if (!item || item.media_proxy_attempted) {
			return;
		}

		item.media_proxy_attempted = true;

		const currentUrl = item.remote_url || '';
		const proxyUrl   = mediaUrls.proxyUrl(item);

		if (!proxyUrl || mediaUrls.isProxyUrl(currentUrl)) {
			return;
		}

		if (currentUrl && !mediaUrls._isS3Url(currentUrl)) {
			return;
		}

		item.remote_url = proxyUrl;
	};

	/**
	 * One-shot retry for a video poster that failed to load.
	 *
	 * @param {Object} item
	 * @return {void}
	 */
	mediaUrls.fallbackThumbnailToProxy = function fallbackThumbnailToProxy(item)
	{
		if (!item || item.media_thumbnail_proxy_attempted) {
			return;
		}

		item.media_thumbnail_proxy_attempted = true;

		const currentUrl = item.thumbnail_image || '';
		const proxyUrl   = mediaUrls.thumbnailProxyUrl(item);

		if (!proxyUrl || mediaUrls.isThumbnailProxyUrl(currentUrl)) {
			return;
		}

		if (currentUrl && !mediaUrls._isS3Url(currentUrl)) {
			return;
		}

		item.thumbnail_image = proxyUrl;
	};

	/**
	 * Build the getPublicMediaUrl endpoint for an item, appending the media
	 * section when the item carries one (version rows do).
	 *
	 * @param {Object} item
	 * @return {string}
	 */
	mediaUrls._lookupUrl = function _lookupUrl(item)
	{
		const mediaId = mediaUrls._mediaId(item);
		let url       = `${Adzuki.base_uri}/Media/getPublicMediaUrl/${mediaId}`;

		if (item && item.section_internal_name) {
			url += `/${encodeURIComponent(item.section_internal_name)}`;
		}

		return url;
	};

	mediaUrls._thumbnailLookupUrl = function _thumbnailLookupUrl(item)
	{
		return `${Adzuki.base_uri}/Media/getPublicThumbnailUrl/${mediaUrls._mediaId(item)}`;
	};

	mediaUrls._captionLookupUrl = function _captionLookupUrl(item)
	{
		return `${Adzuki.base_uri}/Media/getPublicCaptionUrl/${mediaUrls._mediaId(item)}`;
	};

	/**
	 * getPublicS3Url() throws this when the stored location matches no object in
	 * either bucket, which the proxy cannot recover from either.
	 *
	 * @param {string} message
	 * @return {boolean}
	 */
	mediaUrls._isMissingFromS3 = function _isMissingFromS3(message)
	{
		return typeof message === 'string' && (/unable to locate/i).test(message);
	};

	/**
	 * @param {Object} item
	 * @param {boolean} missingFromS3
	 * @return {string}
	 */
	mediaUrls._fallbackUrl = function _fallbackUrl(item, missingFromS3)
	{
		if (missingFromS3) {
			const localUrl = mediaUrls.localAssetUrl(item);

			if (localUrl) {
				return localUrl;
			}
		}

		return mediaUrls.proxyUrl(item) || '';
	};

	/**
	 * Give every remaining item a fallback source once the pass gives up, so the
	 * unresolved tail does not keep rendering unsigned bucket urls.
	 *
	 * @param {Array} items
	 * @param {number} startIndex
	 * @return {void}
	 */
	mediaUrls._assignFallbacksFrom = function _assignFallbacksFrom(items, startIndex)
	{
		let index;

		for (index = startIndex; index < items.length; index++) {
			if (mediaUrls.needsLookup(items[index])) {
				items[index].remote_url = mediaUrls._fallbackUrl(items[index], false);
			}

			if (mediaUrls.needsThumbnailLookup(items[index])) {
				items[index].thumbnail_image = mediaUrls.thumbnailProxyUrl(items[index]);
			}

			if (mediaUrls.needsCaptionLookup(items[index])) {
				items[index].captions_path = mediaUrls.captionProxyUrl(items[index]);
			}
		}
	};

	/**
	 * @param {Object} item
	 * @param {string} lookupUrl
	 * @param {string} successKey
	 * @param {string} fallbackUrl
	 * @param {string} logLabel
	 * @return {Promise<{url: string, failed: boolean}>}
	 */
	mediaUrls._fetchJsonField = async function _fetchJsonField(
		item,
		lookupUrl,
		successKey,
		fallbackUrl,
		logLabel
	) {
		try {
			const response = await fetch(lookupUrl, {
				cache      : 'no-store',
				credentials: 'same-origin',
			});
			const data     = await response.json();

			if (data && data[successKey]) {
				return {
					url   : data[successKey],
					failed: false,
				};
			}

			if (data && data.status === 'error') {
				console.error(logLabel + ' failed:', data.message, mediaUrls._mediaId(item));

				if (successKey === 'remote_url' && mediaUrls._isMissingFromS3(data.message)) {
					return {
						url   : mediaUrls._fallbackUrl(item, true),
						failed: false,
					};
				}

				return {
					url   : fallbackUrl,
					failed: false,
				};
			}

			if (!response.ok) {
				throw new Error(`Error Fetching Public URL: ${response.statusText}`);
			}

			return {
				url   : fallbackUrl,
				failed: false,
			};
		} catch (e) {
			console.error(logLabel + ' request failed:', e, mediaUrls._mediaId(item));

			return {
				url   : fallbackUrl,
				failed: true,
			};
		}
	};

	/**
	 * @param {Object} item
	 * @return {Promise<{url: string, failed: boolean}>}
	 */
	mediaUrls._fetchPublicUrl = async function _fetchPublicUrl(item)
	{
		if (!item) {
			return {
				url   : '',
				failed: false,
			};
		}

		if (!mediaUrls.needsLookup(item)) {
			return {
				url   : item.remote_url || '',
				failed: false,
			};
		}

		return mediaUrls._fetchJsonField(
			item,
			mediaUrls._lookupUrl(item),
			'remote_url',
			mediaUrls._fallbackUrl(item, false),
			'getPublicMediaUrl'
		);
	};

	mediaUrls._fetchThumbnailUrl = async function _fetchThumbnailUrl(item)
	{
		if (!item || !mediaUrls.needsThumbnailLookup(item)) {
			return {
				url   : (item && item.thumbnail_image) || '',
				failed: false,
			};
		}

		return mediaUrls._fetchJsonField(
			item,
			mediaUrls._thumbnailLookupUrl(item),
			'thumbnail_image',
			mediaUrls.thumbnailProxyUrl(item),
			'getPublicThumbnailUrl'
		);
	};

	mediaUrls._fetchCaptionUrl = async function _fetchCaptionUrl(item)
	{
		if (!item || !mediaUrls.needsCaptionLookup(item)) {
			return {
				url   : (item && item.captions_path) || '',
				failed: false,
			};
		}

		return mediaUrls._fetchJsonField(
			item,
			mediaUrls._captionLookupUrl(item),
			'captions_path',
			mediaUrls.captionProxyUrl(item),
			'getPublicCaptionUrl'
		);
	};

	/**
	 * Resolve remote_url, thumbnail_image, and captions_path for one item.
	 * Consecutive network failures are counted once per item, not once per field.
	 *
	 * @param {Object} item
	 * @return {Promise<boolean>} true when a lookup fetch failed at the network layer
	 */
	mediaUrls._resolveItemFields = async function _resolveItemFields(item)
	{
		let failed = false;
		let result;

		if (mediaUrls.needsLookup(item)) {
			result = await mediaUrls._fetchPublicUrl(item);
			item.remote_url = result.url;
			failed = failed || result.failed;
		}

		if (mediaUrls.needsThumbnailLookup(item)) {
			result = await mediaUrls._fetchThumbnailUrl(item);
			item.thumbnail_image = result.url;
			failed = failed || result.failed;
		}

		if (mediaUrls.needsCaptionLookup(item)) {
			result = await mediaUrls._fetchCaptionUrl(item);
			item.captions_path = result.url;
			failed = failed || result.failed;
		}

		return failed;
	};

	mediaUrls.resolve = async function resolve(item)
	{
		const result = await mediaUrls._fetchPublicUrl(item);

		return result.url;
	};

	/**
	 * Sequentially mutate item.remote_url (and thumbnail/caption fields) in place
	 * for each item that needs a presigned URL. A newer call abandons any in-flight
	 * pass. Stops after 3 consecutive network failures so a dead endpoint cannot
	 * starve other work.
	 *
	 * @param {Array} items
	 * @return {Promise<void>}
	 */
	mediaUrls.resolveAll = async function resolveAll(items)
	{
		if (!Array.isArray(items) || items.length === 0) {
			return;
		}

		const runId = ++mediaUrls.runId;
		let consecutiveFailures = 0;
		let index;

		for (index = 0; index < items.length; index++) {
			if (runId !== mediaUrls.runId) {
				return;
			}

			if (!mediaUrls.needsAnyLookup(items[index])) {
				continue;
			}

			const failed = await mediaUrls._resolveItemFields(items[index]);

			if (!failed) {
				consecutiveFailures = 0;
				continue;
			}

			consecutiveFailures++;

			if (consecutiveFailures >= 3) {
				console.warn('Stopped resolving media urls after repeated network failures.');
				mediaUrls._assignFallbacksFrom(items, index + 1);
				return;
			}
		}
	};

	/**
	 * Sequentially mutate version.remote_url in place for each media version
	 * that needs a presigned URL. Version rows carry media_id and
	 * section_internal_name rather than an id, so each lookup targets the
	 * section object. Shares the run-id guard and failure cutoff with
	 * resolveAll so a dead endpoint cannot starve other work.
	 *
	 * @param {Array} versions
	 * @return {Promise<void>}
	 */
	mediaUrls.resolveVersions = async function resolveVersions(versions)
	{
		if (!Array.isArray(versions) || versions.length === 0) {
			return;
		}

		const runId = ++mediaUrls.runId;
		let consecutiveFailures = 0;
		let index;

		for (index = 0; index < versions.length; index++) {
			if (runId !== mediaUrls.runId) {
				return;
			}

			if (!mediaUrls.needsAnyLookup(versions[index])) {
				continue;
			}

			const failed = await mediaUrls._resolveItemFields(versions[index]);

			if (!failed) {
				consecutiveFailures = 0;
				continue;
			}

			consecutiveFailures++;

			if (consecutiveFailures >= 3) {
				console.warn('Stopped resolving media version urls after repeated network failures.');
				mediaUrls._assignFallbacksFrom(versions, index + 1);
				return;
			}
		}
	};

	Adzuki.MediaUrls = mediaUrls;
}(jQuery, window.Adzuki || {}));
